import { defineComponent } from 'vue';
import constants from '../shared/constants';
import {
    getInnerFormElement as getInnerHtmlFormElement,
    initializeTurnstile,
    scrollToField as scrollToInvalidField
} from '../shared/functions';
import type { ApplicationFormInput } from '../shared/types';

export default defineComponent({
    data() {
        return {
            form: {
                inputs: [] as ApplicationFormInput[],
            },
            honeypot: '' as string,
            turnstileCompleted: false as boolean,
        };
    },
    mounted() {
        this.setFormData();
        this.initializeTurnstile();
    },
    methods: {
        getFormElement(): HTMLElement | null {
            return (this.$refs.form as HTMLElement | undefined) ?? null;
        },
        getTurnstileElement(): HTMLElement | null {
            return this.getFormElement()?.querySelector<HTMLElement>(constants.targets.applicationForm.turnstile) ?? null;
        },
        getInnerFormElement(): HTMLFormElement | null {
            return getInnerHtmlFormElement(this.getFormElement());
        },
        initializeTurnstile(): void {
            initializeTurnstile(this.getTurnstileElement(), (completed) => {
                this.turnstileCompleted = completed;
            });
        },
        setFormData(): void {
            const form = this.getFormElement();

            if (!form) return;

            this.form.inputs.push({
                label: form.dataset.labelFirst ?? '',
                name: form.dataset.nameFirst ?? '',
                error: form.dataset.errorFirst ?? '',
                type: 'text',
                required: true,
                pristine: true,
                valid: false,
                value: '',
                group: true,
            });

            this.form.inputs.push({
                label: form.dataset.labelInsertion ?? '',
                name: form.dataset.nameInsertion ?? '',
                short: true,
                type: 'text',
                required: false,
                pristine: true,
                valid: true,
                value: '',
                group: true,
            });

            this.form.inputs.push({
                label: form.dataset.labelLast ?? '',
                name: form.dataset.nameLast ?? '',
                error: form.dataset.errorLast ?? '',
                type: 'text',
                required: true,
                pristine: true,
                valid: false,
                value: '',
                group: true,
            });

            this.form.inputs.push({
                label: form.dataset.labelCity ?? '',
                name: form.dataset.nameCity ?? '',
                error: form.dataset.errorCity ?? '',
                type: 'text',
                required: true,
                pristine: true,
                valid: false,
                value: '',
            });

            this.form.inputs.push({
                label: form.dataset.labelEmail ?? '',
                name: form.dataset.nameEmail ?? '',
                error: form.dataset.errorEmail ?? '',
                type: 'email',
                required: true,
                pristine: true,
                valid: false,
                value: '',
            });

            this.form.inputs.push({
                label: form.dataset.labelPhone ?? '',
                name: form.dataset.namePhone ?? '',
                error: form.dataset.errorPhone ?? '',
                type: 'tel',
                required: true,
                pristine: true,
                valid: false,
                value: '',
            });

            this.form.inputs.push({
                label: form.dataset.labelComment ?? '',
                name: form.dataset.nameComment ?? '',
                type: 'textarea',
                required: false,
                pristine: true,
                valid: true,
                value: '',
            });

            this.form.inputs.push({
                label: form.dataset.labelReferrer ?? '',
                name: form.dataset.nameReferrer ?? '',
                type: 'checkbox-group',
                options: [
                    { label: 'Website', value: 'Website', checked: false },
                    { label: 'Nieuwsbrief', value: 'Nieuwsbrief', checked: false },
                    { label: 'Facebook', value: 'Facebook', checked: false },
                    { label: 'LinkedIn', value: 'LinkedIn', checked: false },
                    { label: 'Instagram', value: 'Instagram', checked: false },
                    { label: 'Twitter', value: 'Twitter', checked: false },
                    { label: 'Collega, vriend of kennis', value: 'Collega, vriend of kennis', checked: false },
                    { label: 'Lokale krant', value: 'Lokale krant', checked: false },
                ],
                required: false,
                pristine: true,
                valid: true,
                value: [],
            });

            this.form.inputs.push({
                label: form.dataset.labelSmartonnections ?? '',
                name: form.dataset.nameSmartonnections ?? '',
                error: form.dataset.errorSmartonnections ?? '',
                type: 'checkbox',
                required: true,
                pristine: true,
                valid: false,
                value: false,
            });

            this.honeypot = form.dataset.nameText ?? '';
        },
        setCheckboxGroup(input: ApplicationFormInput): void {
            const field = document.getElementById(`${input.name}Field`);
            const checkedInputs = Array.from(
                field?.querySelectorAll<HTMLInputElement>('input[type="checkbox"]:checked') ?? []
            );

            input.pristine = false;
            input.value = checkedInputs.map((checkbox) => checkbox.value);

            input.options?.forEach((option) => {
                option.checked = checkedInputs.some((checkbox) => checkbox.value === option.value);
            });

            this.validateInput(input);
        },
        validateInput(input: ApplicationFormInput): void {
            if (!input.required) return;

            switch (input.type) {
                case 'text':
                    input.valid = Boolean(input.value);
                    break;
                case 'email': {
                    const sanitized = String(input.value).replace(/\s+/g, '').trim();
                    const emailRegex = /\S+@\S+\.\S+/;

                    input.value = sanitized;
                    input.valid = emailRegex.test(sanitized);
                    break;
                }
                case 'tel': {
                    const sanitized = String(input.value).replace(/\D/g, '').trim();

                    input.value = sanitized;
                    input.valid = sanitized.length === 10;
                    break;
                }
                case 'textarea':
                    input.valid = Boolean(input.value) && String(input.value).length <= 3800;
                    break;
                case 'checkbox-group':
                    input.valid = Array.isArray(input.value) && input.value.length > 0;
                    break;
                case 'checkbox':
                    input.valid = Boolean(input.value);
                    break;
                default:
                    input.valid = Boolean(input.value);
                    break;
            }
        },
        validateForm(): void {
            this.form.inputs.forEach((input) => {
                input.pristine = false;
            });

            const firstInvalidInput = this.form.inputs.find((input) => input.required && !input.valid);

            if (!firstInvalidInput) {
                this.submitForm();
                return;
            }

            scrollToInvalidField(`${firstInvalidInput.name}Field`);
        },
        submitForm(): void {
            this.getInnerFormElement()?.submit();
        },
    },
});
